import { SubscriptionStatus } from "@/clients/models/SubscriptionStatus";
import { OutlinedButton } from "@/components/ui/buttons/OutlinedButton";
import { env } from "@/constants/env";
import { useCustomerInfo } from "@/features/settings/hooks/use-customer-info";
import { useGetSubscriptionStatus } from "@/hooks/requests/user/use-get-subscription-status";
import { cn } from "@/utils/cn";
import { openContactEmail } from "@/features/user-profile/utils/support-links";
import { openSubscriptionManagement } from "@/utils/open-subscription-management";
import { textVariants } from "@/utils/text-variants";
import { Capacitor } from "@capacitor/core";
import type { CustomerInfo } from "@revenuecat/purchases-capacitor";
import { differenceInDays, format, isValid, parseISO } from "date-fns";
import { memo, useMemo } from "react";

const getPlanTitleFromServerType = (subscriptionType?: string | null) => {
  switch (subscriptionType?.toLowerCase()) {
    case "monthly":
      return "Monthly Subscription";
    case "yearly":
      return "Yearly Subscription";
    case "free":
      return "Free Plan";
    default:
      return "Free Plan";
  }
};

const formatDateLabel = (rawDate?: string | null) => {
  if (!rawDate) return undefined;
  const parsed = parseISO(rawDate);
  if (!isValid(parsed)) return undefined;
  return format(parsed, "MMM d, yyyy");
};

const pillButtonClass =
  "text-[#620ad5] border-[#ebe0fb] shadow-[0px_8px_64.7px_0px_rgba(64,0,255,0.1)] !h-[52px]";

export const SubscriptionManagementContent = memo(function SubscriptionManagementContent({
  onUpgrade,
}: {
  onUpgrade?: () => void;
}) {
  const isStagingIos = env.VITE_ENVIRONMENT === "staging" && Capacitor.getPlatform() === "ios";
  const { customerInfo } = useCustomerInfo();
  const { subscriptionStatus } = useGetSubscriptionStatus({ enabled: true });

  const normalizedCustomerInfo: CustomerInfo | null = useMemo(() => {
    if (!customerInfo) return null;
    return (
      (customerInfo as { customerInfo?: CustomerInfo }).customerInfo ??
      (customerInfo as CustomerInfo)
    );
  }, [customerInfo]);

  const managementURL = normalizedCustomerInfo?.managementURL;
  const status = subscriptionStatus?.status ?? SubscriptionStatus.NONE;
  const planTitle = getPlanTitleFromServerType(subscriptionStatus?.subscription_type);

  const dateLabel =
    formatDateLabel(subscriptionStatus?.expired_at) ??
    formatDateLabel(normalizedCustomerInfo?.latestExpirationDate);

  const trialDaysLeft = useMemo(() => {
    if (status !== SubscriptionStatus.TRIAL || !subscriptionStatus?.expired_at) return null;
    const expiry = parseISO(subscriptionStatus.expired_at);
    if (!isValid(expiry)) return null;
    return Math.max(0, differenceInDays(expiry, new Date()));
  }, [status, subscriptionStatus?.expired_at]);

  const timelineText = useMemo(() => {
    if (status === SubscriptionStatus.EXPIRED || subscriptionStatus?.is_expired)
      return `Expired ${dateLabel}`;
    if (status === SubscriptionStatus.CANCELLED) return `Expires ${dateLabel}`;
    if (status === SubscriptionStatus.TRIAL) {
      if (trialDaysLeft !== null)
        return `Ends in ${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""}.`;
      if (dateLabel) return `Trial ends ${dateLabel}.`;
      return null;
    }
    if (dateLabel) return `Renews ${dateLabel}.`;
    return null;
  }, [dateLabel, status, subscriptionStatus?.is_expired, trialDaysLeft]);

  return (
    <div className="safe-area-bottom flex flex-1 flex-col gap-6 px-9 pt-6 pb-4">
      {/* Plan card */}
      <div className="flex flex-col items-center gap-2 rounded-[28px] bg-[#130c2d] px-8 py-8">
        <p className="text-center text-[26px] font-bold leading-tight text-white">{planTitle}</p>
        {timelineText && (
          <p className="text-center text-base text-white/80">{timelineText}</p>
        )}
        <button
          type="button"
          className="mt-2 rounded-full bg-[#620ad5] px-8 py-2.5 text-sm font-medium text-white"
          onClick={onUpgrade}
        >
          Upgrade Plan
        </button>
      </div>

      {/* Other */}
      <p className={cn(textVariants.bodySmall, "font-bold text-black")}>Other</p>
      <div className="flex flex-col gap-4">
        <OutlinedButton
          bgColor="white"
          color="gray"
          textCase="normal"
          borderRadius="wide"
          className={pillButtonClass}
          onClick={() => openSubscriptionManagement(managementURL)}
        >
          Manage subscriptions in phone settings
        </OutlinedButton>
        {isStagingIos && (
          <p className="text-xs leading-relaxed text-gray-500">
            Staging uses Apple Sandbox subscriptions. If not opened automatically, go to
            Settings &gt; Developer &gt; Sandbox Apple Account &gt; Manage &gt; Subscriptions.
          </p>
        )}
        <OutlinedButton
          bgColor="white"
          color="gray"
          textCase="normal"
          borderRadius="wide"
          className={pillButtonClass}
          onClick={() => openContactEmail("Help with my subscription")}
        >
          Contact Us
        </OutlinedButton>
      </div>
    </div>
  );
});
